import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Lightbox } from '@/components/Lightbox';
import { getProject } from '@/lib/api';
import { SEO } from '@/components/SEO';
import { StructuredData } from '@/components/StructuredData';

// Behind the Scenes Gallery Component
function BehindTheScenesGallery({ items }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 6;
  const totalPages = Math.ceil(items.length / itemsPerPage);
  
  const visibleItems = items.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const openLightbox = (indexInPage) => {
    const actualIndex = currentPage * itemsPerPage + indexInPage;
    setSelectedIndex(actualIndex);
    setLightboxOpen(true);
  };

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const isVideo = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  };

  return (
    <>
      <div className="relative">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {visibleItems.map((item, index) => (
            <button
              key={currentPage * itemsPerPage + index}
              onClick={() => openLightbox(index)}
              className="relative aspect-video rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] group cursor-pointer"
              data-testid={`bts-item-${currentPage * itemsPerPage + index}`}
            >
              {isVideo(item) ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center group-hover:bg-white/30 transition-all">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white" className="ml-1">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              ) : (
                <img
                  src={item}
                  alt={`Behind the scenes ${currentPage * itemsPerPage + index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
            </button>
          ))}
        </div>

        {/* Navigation Arrows */}
        {totalPages > 1 && (
          <>
            {currentPage > 0 && (
              <button
                onClick={prevPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-10"
                aria-label="Previous images"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
            )}
            
            {currentPage < totalPages - 1 && (
              <button
                onClick={nextPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-10"
                aria-label="Next images"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            )}

            {/* Page Indicator */}
            <div className="text-center mt-4">
              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">
                {currentPage * itemsPerPage + 1}-{Math.min((currentPage + 1) * itemsPerPage, items.length)} of {items.length}
              </p>
            </div>
          </>
        )}
      </div>

      {lightboxOpen && (
        <Lightbox
          items={items}
          initialIndex={selectedIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}


export default function WorkDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [similarProjects, setSimilarProjects] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    // Check if preview mode
    const urlParams = new URLSearchParams(window.location.search);
    const preview = urlParams.get('preview') === 'true';
    setIsPreview(preview);
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        
        // Load project with preview parameter
        const projectRes = await fetch(`${backendUrl}/api/projects/${slug}?preview=${preview}`);
        if (!projectRes.ok) throw new Error('Project not found');
        const proj = await projectRes.json();
        setProject(proj);

        // Load capabilities for mapping IDs to names
        const capsRes = await fetch(`${backendUrl}/api/admin/capabilities`);
        const caps = await capsRes.json();
        setCapabilities(caps);

        // Load similar projects if IDs exist
        if (proj.similar_project_ids && proj.similar_project_ids.length > 0) {
          const projectsRes = await fetch(`${backendUrl}/api/projects`);
          const allProjects = await projectsRes.json();
          const similar = allProjects.filter(p => proj.similar_project_ids.includes(p.id));
          setSimilarProjects(similar);
        }
      } catch (err) {
        setError('Project not found');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  // Helper to get video embed URL
  const getVideoEmbedUrl = () => {
    if (!project.video_url) return null;
    if (project.video_platform === 'youtube') {
      return `https://www.youtube.com/embed/${project.video_url}`;
    }
    return `https://player.vimeo.com/video/${project.video_url}?h=0&title=0&byline=0&portrait=0&color=c9a96e`;
  };

  // Helper to get header image based on selection
  const getHeaderImage = () => {
    if (project.header_image_source === 'poster') return project.poster;
    if (project.header_image_source === 'thumbnail') return project.thumbnail;
    if (project.header_image_source === 'custom') return project.hero_image;
    return project.poster || project.thumbnail || project.hero_image;
  };

  if (loading) {
    return (
      <div className="pt-32 max-w-[1200px] mx-auto px-5 sm:px-8">
        <Skeleton className="w-full aspect-video rounded-lg mb-8" />
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton className="h-6 w-1/2" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="pt-40 text-center">
        <p className="font-body text-[hsl(var(--bk-fg-muted))]">Project not found.</p>
        <Link to="/work" className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] mt-4">
          <ArrowLeft size={14} /> Back to work
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={`${project.title} | Baklight Films ${project.year}`}
        description={project.logline || project.project_definition || project.description || `${project.category} film project by Baklight Films - ${project.title}. Director: Paco Herrera. ${project.year}.`}
        path={`/work/${project.slug}`}
        image={project.og_image || project.thumbnail || project.poster || project.hero_image}
        type="article"
        keywords={[
          project.category,
          project.title,
          'Baklight Films',
          'Paco Herrera',
          project.year,
          'Mexico film production',
          'cinematography',
          ...(capabilities.filter(c => project.capabilities?.includes(c.id)).map(c => c.name)),
          ...(project.tags || [])
        ].join(', ')}
        publishedTime={project.published_at}
        modifiedTime={project.updated_at}
      />
      
      {/* CreativeWork Structured Data */}
      <StructuredData 
        type="project" 
        data={{
          title: project.title,
          description: project.logline || project.project_definition,
          image: project.poster || project.thumbnail,
          year: project.year,
          category: project.category,
          video_url: project.video_url,
          published_at: project.published_at,
          duration: project.duration,
          language: project.language || 'es',
          keywords: [
            project.category,
            'Mexico',
            'Baklight Films',
            ...(capabilities.filter(c => project.capabilities?.includes(c.id)).map(c => c.name))
          ].join(', ')
        }}
      />
      
      {/* Breadcrumb Structured Data */}
      <StructuredData 
        type="breadcrumb" 
        data={{
          items: [
            { name: 'Home', url: window.location.origin },
            { name: 'Work', url: `${window.location.origin}/work` },
            { name: project.title, url: `${window.location.origin}/work/${project.slug}` }
          ]
        }}
      />
      
      {/* Preview Banner */}
      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-600 text-black py-2 px-4 text-center font-mono-meta text-sm">
          <strong>PREVIEW MODE</strong> - This project is not published yet
        </div>
      )}
      
      {/* Hero */}
      <section className={`relative h-[30vh] min-h-[350px] flex items-end overflow-hidden ${isPreview ? 'mt-10' : ''}`}>
        <div className="absolute inset-0">
          <img
            src={getHeaderImage()}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222,18%,4%)] via-[hsl(222,18%,6%)]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 pb-12 w-full">
          <Link to="/work" className="inline-flex items-center gap-2 font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-6 hover:text-[hsl(var(--bk-fg))] transition-colors">
            <ArrowLeft size={14} /> Back to work
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.88] text-[hsl(var(--bk-fg))]">
            {project.title}
          </h1>
          <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mt-4">
            {project.year} {project.location && `• ${project.location}`}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-14">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-12">
              <SectionReveal>
                {project.logline && (
                  <p className="font-display text-xl md:text-2xl text-[hsl(var(--bk-fg))] leading-snug mb-10">
                    {project.logline}
                  </p>
                )}

                {/* Video Embed */}
                {project.video_url && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-10 shadow-[0_16px_60px_rgba(0,0,0,0.5)]">
                    <iframe
                      src={getVideoEmbedUrl()}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={project.title}
                    />
                  </div>
                )}
              </SectionReveal>

              {/* NEW STRUCTURED SECTIONS */}
              {project.project_definition && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">PROJECT DEFINITION</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.project_definition}
                  </p>
                </SectionReveal>
              )}

              {project.client_funding && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">CLIENT / FUNDING</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.client_funding}
                  </p>
                </SectionReveal>
              )}

              {project.agency && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">AGENCY</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.agency}
                  </p>
                </SectionReveal>
              )}

              {project.challenge && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">THE CHALLENGE</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.challenge}
                  </p>
                </SectionReveal>
              )}

              {project.our_role && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">OUR ROLE</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.our_role}
                  </p>
                </SectionReveal>
              )}

              {project.approach && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">THE APPROACH</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.approach}
                  </p>
                </SectionReveal>
              )}

              {project.result && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">THE RESULT</h3>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65] whitespace-pre-wrap">
                    {project.result}
                  </p>
                </SectionReveal>
              )}

              {/* Legacy description fallback */}
              {!project.project_definition && project.description && (
                <SectionReveal>
                  <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-[1.65]">
                    {project.description}
                  </p>
                </SectionReveal>
              )}

              {/* Gallery */}
              {project.gallery && project.gallery.length > 0 && (
                <SectionReveal>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {project.gallery.map((img, i) => (
                      <div key={i} className="aspect-video rounded-lg overflow-hidden">
                        <img src={img} alt={`${project.title} gallery ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                </SectionReveal>
              )}

              {/* RECOGNITION */}
              {project.recognition && project.recognition.length > 0 && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">RECOGNITION</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                    {project.recognition.map((award, i) => (
                      <div key={i} className="text-center space-y-2">
                        {award.image && (
                          <img src={award.image} alt={award.award_name} className="h-20 mx-auto object-contain" />
                        )}
                        <div>
                          <p className="font-body text-sm text-[hsl(var(--bk-fg))]">{award.award_name}</p>
                          {award.category && (
                            <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">{award.category}</p>
                          )}
                          <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
                            {award.year} • {award.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionReveal>
              )}

              {/* CREDITS */}
              {project.credits && project.credits.length > 0 && (
                <SectionReveal>
                  <h3 className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">CREDITS</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {project.credits.filter(c => c.name).map((credit, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-[hsl(var(--bk-line))]">
                        <span className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">{credit.role}</span>
                        <span className="font-body text-sm text-[hsl(var(--bk-fg))]">{credit.name}</span>
                      </div>
                    ))}
                  </div>
                </SectionReveal>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <SectionReveal delay={0.15}>
                {/* PROJECT DETAILS (was Highlights) */}
                {((project.highlights && project.highlights.length > 0) || project.logline) && (
                  <div className="mb-8">
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">PROJECT DETAILS</p>
                    <div className="space-y-3">
                      {project.highlights && project.highlights.map((h, i) => (
                        <p key={i} className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">{h}</p>
                      ))}
                    </div>
                  </div>
                )}

                <Separator className="mb-8 bg-[hsl(var(--bk-line))]" />

                {/* OUR SERVICES (was Services) */}
                {project.capabilities && project.capabilities.length > 0 && capabilities.length > 0 && (
                  <div className="mb-8">
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">OUR SERVICES</p>
                    <div className="flex flex-wrap gap-2">
                      {project.capabilities.map((capId, i) => {
                        const cap = capabilities.find(c => c.id === capId);
                        return cap ? (
                          <Badge key={i} variant="secondary" className="font-mono-meta bg-[hsl(var(--bk-bg-elev))] text-[hsl(var(--bk-fg-muted))] border border-[hsl(var(--bk-line))]">
                            {cap.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Legacy services fallback */}
                {(!project.capabilities || project.capabilities.length === 0) && project.services && project.services.length > 0 && (
                  <div className="mb-8">
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-4">OUR SERVICES</p>
                    <div className="flex flex-wrap gap-2">
                      {project.services.map((s, i) => (
                        <Badge key={i} variant="secondary" className="font-mono-meta bg-[hsl(var(--bk-bg-elev))] text-[hsl(var(--bk-fg-muted))] border border-[hsl(var(--bk-line))]">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator className="mb-8 bg-[hsl(var(--bk-line))]" />

                {/* CTA */}
                <div>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-4">
                    Interested in working with us on a similar project?
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 font-body text-sm px-5 py-2.5 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98]"
                  >
                    Start a Project <ArrowRight size={14} />
                  </Link>
                </div>

                {/* BEHIND THE SCENES GALLERY */}
                {project.behind_the_scenes_gallery && project.behind_the_scenes_gallery.length > 0 && (
                  <div className="mt-12">
                    <h3 className="font-display text-xl sm:text-2xl text-[hsl(var(--bk-fg))] mb-6">
                      Behind the Scenes
                    </h3>
                    <BehindTheScenesGallery items={project.behind_the_scenes_gallery} />
                  </div>
                )}
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>

      {/* SIMILAR PROJECTS */}
      {similarProjects.length > 0 && (
        <section className="py-8 sm:py-10 bg-[hsl(var(--bk-bg-elev))] border-t border-[hsl(var(--bk-line))]">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <SectionReveal>
              <h2 className="font-display text-3xl sm:text-4xl text-[hsl(var(--bk-fg))] mb-6">Similar Projects</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {similarProjects.map((proj) => (
                  <Link key={proj.id} to={`/work/${proj.slug}`} className="group">
                    <div className="aspect-video rounded-lg overflow-hidden mb-3">
                      <img 
                        src={proj.thumbnail || proj.poster} 
                        alt={proj.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="font-body text-base text-[hsl(var(--bk-fg))] group-hover:text-[hsl(var(--bk-accent))] transition-colors">
                      {proj.title}
                    </h3>
                    <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))] mt-1">{proj.year}</p>
                  </Link>
                ))}
              </div>
            </SectionReveal>
          </div>
        </section>
      )}
    </div>
  );
}
